import { useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router"
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { apiClient } from "@/lib/api"

type ActivationState = "loading" | "success" | "already_active" | "error"

export function ActivateAccountPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token") || ""

  const [state, setState] = useState<ActivationState>("loading")
  const [email, setEmail] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    if (!token) {
      setState("error")
      setErrorMessage("Activation link is invalid or missing.")
      return
    }

    let cancelled = false
    apiClient.activateAccount(token).then((result) => {
      if (cancelled) return
      if (result.ok) {
        setState(result.already_active ? "already_active" : "success")
        if (result.email) setEmail(result.email)
      } else {
        setState("error")
        setErrorMessage(result.error || "Activation failed. Please try again.")
      }
    }).catch(() => {
      if (!cancelled) {
        setState("error")
        setErrorMessage("Something went wrong. Please try again.")
      }
    })

    return () => { cancelled = true }
  }, [token])

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-[400px] text-center">
        {state === "loading" && (
          <>
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
            </div>
            <h1 className="text-xl font-semibold text-foreground mb-2">Activating your account…</h1>
            <p className="text-sm text-muted-foreground">This will only take a moment.</p>
          </>
        )}

        {(state === "success" || state === "already_active") && (
          <>
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <CheckCircle2 className="h-7 w-7 text-primary" />
            </div>
            <h1 className="text-xl font-semibold text-foreground mb-2">
              {state === "already_active" ? "Already activated" : "You're in!"}
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed mb-8">
              {state === "already_active"
                ? "Your account is already active. You can sign in below."
                : "Your email is confirmed and your account is ready."}
            </p>
            <Button asChild className="w-full">
              <Link to={email ? `/login?email=${encodeURIComponent(email)}` : "/login"}>
                Sign in
              </Link>
            </Button>
          </>
        )}

        {state === "error" && (
          <>
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <h1 className="text-xl font-semibold text-foreground mb-2">Activation failed</h1>
            <p className="text-sm text-muted-foreground leading-relaxed mb-8">
              {errorMessage}
            </p>
            <div className="space-y-3">
              <Button asChild variant="outline" className="w-full">
                <Link to="/sign-up">Create a new account</Link>
              </Button>
              <Button asChild variant="ghost" className="w-full">
                <Link to="/login">Sign in</Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
